/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailCache.js
 * @description
 * Module-level (non-React) message cache + prefetch layer for the in-app mail
 * client. It exists so that tapping a mail push notification lands on a
 * READABLE e-mail as fast as physically possible:
 *
 *  - It lives OUTSIDE the React tree, so it survives before/after the provider
 *    mount. A push tap can therefore start the body fetch while the app is
 *    still booting/splashing (no navigator yet), and by the time the reader
 *    mounts the message is already in memory.
 *  - {@link primeMailStubFromPush} turns an FCM/local notification payload into
 *    a synthetic message so the reader can paint sender + subject + preview on
 *    its FIRST frame, with zero awaits.
 *  - Every list/thread fetch primes it ({@link primeMailMessages} /
 *    {@link cacheMailThread}), so opening a mail from the list is instant too.
 *  - A tiny AsyncStorage mirror carries the push stub and the last few
 *    prefetched bodies ACROSS PROCESSES: Android delivers background pushes to a
 *    headless JS runtime that does not share module memory with the UI process,
 *    so without the mirror a prefetch done in the background would be lost.
 *
 * Bounded on purpose: at most {@link MAX_MESSAGES} messages / {@link MAX_THREADS}
 * threads (oldest evicted first), everything TTL'd. Nothing here ever throws —
 * a failed prefetch silently degrades to the screen's own fetch.
 *
 * Pure JS: no React import, safe to import from `index.js`, background tasks and
 * notification handlers alike.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as mailApi from '../services/mailApi';

/** Maximum number of individual messages kept in memory. @type {number} */
const MAX_MESSAGES = 60;
/** Maximum number of conversations kept in memory. @type {number} */
const MAX_THREADS = 20;
/** How long a cached entry may be served before it is considered stale (ms). @type {number} */
const TTL_MS = 10 * 60 * 1000;
/** How long a cached entry survives on disk (ms) — shorter, it only serves the push fast-path. */
const DISK_TTL_MS = 15 * 60 * 1000;

const DISK_STUB_KEY = '@mail_cache_push_stub';
const DISK_MESSAGE_PREFIX = '@mail_cache_msg:';
const DISK_INDEX_KEY = '@mail_cache_msg_index';
/** How many full message bodies are mirrored to disk (they can be large). */
const MAX_DISK_MESSAGES = 5;
/** Never mirror a message bigger than this to AsyncStorage (bytes of JSON). */
const MAX_DISK_BYTES = 192 * 1024;

/** @type {Map<string, {message: Object, at: number}>} id -> entry (insertion-ordered = LRU-ish). */
const messages = new Map();
/** @type {Map<string, {items: Array<Object>, at: number}>} threadKey -> entry. */
const threads = new Map();
/** @type {Map<string, Promise<*>>} de-dupes concurrent prefetches. */
const inflight = new Map();

/**
 * Stable id resolver, mirroring `msgId()` in MailThreadScreen / MailHomeScreen.
 * @param {Object} [message] Normalized mail message.
 * @returns {string} The message id, or '' when it cannot be resolved.
 */
const idOf = (message) => String(message?.id || message?._id || '').trim();

/**
 * Coerce anything into a trimmed string (FCM data values always arrive as strings,
 * but local notification payloads and callers are less disciplined).
 * @param {*} value
 * @returns {string}
 */
const str = (value) => (value === undefined || value === null ? '' : String(value).trim());

/**
 * Pick the first non-empty value for any of the given keys — used to read FCM
 * payloads that may use camelCase or all-lowercase keys.
 * @param {Object} source Payload object.
 * @param {...string} keys Candidate keys, most specific first.
 * @returns {string} First non-empty value, or ''.
 */
const pick = (source, ...keys) => {
    if (!source || typeof source !== 'object') {
        return '';
    }
    for (const key of keys) {
        const value = str(source[key]);
        if (value) {
            return value;
        }
    }

    return '';
};

/**
 * Reduce an arbitrary snippet (HTML, markdown, entity-encoded junk) to a clean
 * single-line PLAIN-TEXT preview, Gmail / Apple Mail style.
 *
 * Defensive by design: the backend is expected to send an already-stripped
 * `preview`, but an unsanitized payload must never leak `<img …>` tags, `<div>`
 * soup or markdown syntax into a notification banner or into the reader's first
 * paint — which is exactly what the "image tags / markdown junk in the push
 * text" report was about.
 *
 * @param {*} value Raw snippet of any shape.
 * @param {number} [maxLength=180] Hard character cap (an ellipsis is appended).
 * @returns {string} Clean, whitespace-collapsed preview text.
 */
export function toPlainMailPreview(value, maxLength = 180) {
    try {
        let text = str(value);
        if (!text) {
            return '';
        }

        // Drop non-content blocks entirely (their text is never part of a preview).
        text = text.replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, ' ');
        // Markdown images/links: keep the human-readable part, drop the URL.
        text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
        text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
        // Block-ish tags become spaces so words never get glued together.
        text = text.replace(/<\s*(br|\/p|\/div|\/tr|\/li|\/h[1-6])\s*\/?>/gi, ' ');
        // Everything else HTML simply goes away.
        text = text.replace(/<[^>]*>/g, ' ');
        // The handful of entities that actually show up in mail previews.
        text = text
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#0?39;|&apos;/gi, "'");
        // Leftover markdown decoration + quote markers.
        text = text.replace(/[*_`~]{1,3}/g, '');
        text = text.replace(/^\s*[>#]+\s?/gm, '');
        // Collapse every run of whitespace into a single space.
        text = text.replace(/\s+/g, ' ').trim();

        if (maxLength > 0 && text.length > maxLength) {
            return `${text.slice(0, maxLength).trimEnd()}…`;
        }

        return text;
    } catch (_e) {
        return '';
    }
}

/**
 * Shallow copy without `undefined` values, so a partial update can never blank
 * an already-cached field by spreading `undefined` over it.
 * @param {Object} source
 * @returns {Object}
 */
const dropUndefined = (source) => {
    const out = {};
    if (!source || typeof source !== 'object') {
        return out;
    }
    Object.keys(source).forEach((key) => {
        if (source[key] !== undefined) {
            out[key] = source[key];
        }
    });

    return out;
};

/**
 * Rough "how complete is this message" score. Used so a preview-only stub can
 * never overwrite an already-fetched full body.
 * @param {Object} [message]
 * @returns {number}
 */
const bodyScore = (message) => {
    if (!message || typeof message !== 'object') {
        return -1;
    }
    if (message.__mailStub) {
        return 0;
    }
    const html = typeof message.bodyHtml === 'string' ? message.bodyHtml.length : 0;
    const text = typeof message.bodyText === 'string' ? message.bodyText.length : 0;
    const preview = typeof message.previewText === 'string' ? message.previewText.length : 0;
    const attachments = Array.isArray(message.attachments) ? message.attachments.length : 0;

    return (html + text) * 10 + preview + attachments;
};

/**
 * Merge an incoming message over a cached one, keeping the FULLER body.
 * @param {Object} [prev] Cached message.
 * @param {Object} next Incoming message (may be a stub / list row).
 * @returns {Object} Merged message.
 */
const mergeMessage = (prev, next) => {
    const clean = dropUndefined(next);
    if (!prev) {
        return clean;
    }

    const merged = {...prev, ...clean};

    if (bodyScore(prev) > bodyScore(clean)) {
        // The cached copy is richer (full body) than the incoming stub/list row:
        // keep its body + attachments, adopt only the metadata from `clean`.
        merged.bodyHtml = prev.bodyHtml;
        merged.bodyText = prev.bodyText;
        merged.previewText = prev.previewText || clean.previewText;
        if (Array.isArray(prev.attachments)
            && prev.attachments.length >= (Array.isArray(clean.attachments) ? clean.attachments.length : 0)) {
            merged.attachments = prev.attachments;
        }
    }

    if (!clean.__mailStub) {
        // A real message never stays flagged as a placeholder.
        delete merged.__mailStub;
    }

    return merged;
};

/**
 * Insert/refresh a Map entry and evict the oldest ones past `max`.
 * @param {Map<string, Object>} map
 * @param {string} key
 * @param {Object} entry
 * @param {number} max
 * @returns {void}
 */
const setBounded = (map, key, entry, max) => {
    if (map.has(key)) {
        map.delete(key); // re-insert so Map insertion order stays LRU-ish
    }
    map.set(key, entry);
    while (map.size > max) {
        const oldest = map.keys().next();
        if (oldest.done) {
            break;
        }
        map.delete(oldest.value);
    }
};

/**
 * Whether a cache entry is still inside its TTL.
 * @param {{at?: number}} [entry]
 * @param {number} [ttlMs]
 * @returns {boolean}
 */
const isFresh = (entry, ttlMs = TTL_MS) => !!entry && (Date.now() - Number(entry.at || 0)) < ttlMs;

/* ───────────────────────── disk mirror (cross-process) ───────────────────── */

/**
 * Best-effort mirror of a full message to AsyncStorage so a prefetch performed
 * in the Android headless push process is still available to the UI process.
 * Silently skips oversized bodies. Never throws.
 * @param {Object} message
 * @returns {Promise<void>}
 */
const persistMessage = async (message) => {
    try {
        const id = idOf(message);
        if (!id) {
            return;
        }
        const json = JSON.stringify({at: Date.now(), message});
        if (!json || json.length > MAX_DISK_BYTES) {
            return;
        }
        await AsyncStorage.setItem(`${DISK_MESSAGE_PREFIX}${id}`, json);

        let ids = [];
        try {
            const raw = await AsyncStorage.getItem(DISK_INDEX_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            ids = Array.isArray(parsed) ? parsed : [];
        } catch (_e) {
            ids = [];
        }
        const next = [id, ...ids.filter((value) => value !== id)];
        const keep = next.slice(0, MAX_DISK_MESSAGES);
        const drop = next.slice(MAX_DISK_MESSAGES);
        await AsyncStorage.setItem(DISK_INDEX_KEY, JSON.stringify(keep));
        if (drop.length) {
            await AsyncStorage.multiRemove(drop.map((value) => `${DISK_MESSAGE_PREFIX}${value}`));
        }
    } catch (_e) {
        // Mirroring is a pure optimisation — a failure must never surface.
    }
};

/**
 * Load the disk mirror (push stub + last prefetched bodies) back into memory.
 * Called once at module load; awaiting it is optional (the memory cache simply
 * fills in slightly later). Never throws.
 * @returns {Promise<boolean>} true when at least one entry was restored.
 */
export const hydrateMailCacheFromDisk = async () => {
    let restored = false;
    try {
        const stubRaw = await AsyncStorage.getItem(DISK_STUB_KEY);
        if (stubRaw) {
            const parsed = JSON.parse(stubRaw);
            if (parsed?.message && isFresh(parsed, DISK_TTL_MS)) {
                primeMailMessage(parsed.message);
                restored = true;
            }
        }
    } catch (_e) {
        // ignore malformed/absent stub
    }

    try {
        const raw = await AsyncStorage.getItem(DISK_INDEX_KEY);
        const ids = raw ? JSON.parse(raw) : [];
        if (Array.isArray(ids) && ids.length) {
            const pairs = await AsyncStorage.multiGet(ids.map((id) => `${DISK_MESSAGE_PREFIX}${id}`));
            (Array.isArray(pairs) ? pairs : []).forEach(([, value]) => {
                try {
                    const parsed = value ? JSON.parse(value) : null;
                    if (parsed?.message && isFresh(parsed, DISK_TTL_MS)) {
                        primeMailMessage(parsed.message);
                        restored = true;
                    }
                } catch (_e) {
                    // ignore one bad entry
                }
            });
        }
    } catch (_e) {
        // ignore malformed/absent mirror
    }

    return restored;
};

/* ───────────────────────── public cache API ───────────────────────── */

/**
 * Store a single message, shallow-merged over whatever is cached. A preview-only
 * stub NEVER overwrites an already-fetched full body.
 * @param {Object} message Normalized mail message (`id`/`_id` required).
 * @returns {Object|null} The merged message that is now cached, or null.
 */
export function primeMailMessage(message) {
    try {
        const id = idOf(message);
        if (!id) {
            return null;
        }
        const prev = messages.get(id);
        const merged = mergeMessage(prev?.message, message);
        setBounded(messages, id, {message: merged, at: Date.now()}, MAX_MESSAGES);

        return merged;
    } catch (_e) {
        return null;
    }
}

/**
 * Store a list of messages (a list page, a thread, search results…).
 * @param {Array<Object>} list Messages to cache; non-arrays are ignored.
 * @returns {number} How many messages were cached.
 */
export function primeMailMessages(list) {
    if (!Array.isArray(list) || !list.length) {
        return 0;
    }
    let count = 0;
    list.forEach((message) => {
        if (primeMailMessage(message)) {
            count += 1;
        }
    });

    return count;
}

/**
 * Read a cached message.
 * @param {string} id Message id.
 * @returns {Object|null} The cached message, or null when absent/stale.
 */
export function getCachedMailMessage(id) {
    try {
        const key = str(id);
        if (!key) {
            return null;
        }
        const entry = messages.get(key);
        if (!isFresh(entry)) {
            return null;
        }

        return entry.message || null;
    } catch (_e) {
        return null;
    }
}

/**
 * Shallow-patch an already-cached message in place (memory only). Used by the
 * background notification actions (mark as read / archive from the shade) so
 * the mutation is reflected the moment the reader opens, without waiting for a
 * refetch. A cache miss is a silent no-op; never throws.
 *
 * @param {string} id Message id to patch.
 * @param {Object} patch Fields merged over the cached copy (e.g. `{isRead: true}`).
 * @returns {Object|null} The patched message now cached, or null when not cached.
 */
export function patchCachedMailMessage(id, patch) {
    try {
        const key = str(id);
        if (!key || !patch || typeof patch !== 'object') {
            return null;
        }
        const entry = messages.get(key);
        if (!entry?.message) {
            return null;
        }
        entry.message = {...entry.message, ...dropUndefined(patch)};

        return entry.message;
    } catch (_e) {
        return null;
    }
}

/**
 * Forget a cached message (memory only) — used when a message was deleted
 * elsewhere (another client / a colleague on a shared account), so it can never
 * re-open from a stale copy. Never throws.
 * @param {string} id Message id.
 * @returns {boolean} True when an entry was removed.
 */
export function dropCachedMailMessage(id) {
    try {
        const key = str(id);

        return key ? messages.delete(key) : false;
    } catch (_e) {
        return false;
    }
}

/**
 * Cache a whole conversation (also primes every item individually).
 * @param {string} threadKey Conversation key.
 * @param {Array<Object>} items Messages of the thread.
 * @returns {Array<Object>|null} The cached items, or null when nothing was stored.
 */
export function cacheMailThread(threadKey, items) {
    try {
        const key = str(threadKey);
        const list = Array.isArray(items) ? items : [];
        if (!key || !list.length) {
            return null;
        }
        primeMailMessages(list);
        // Re-read through the message cache so the thread never carries a
        // thinner body than the one already fetched for a single message.
        const merged = list.map((item) => getCachedMailMessage(idOf(item)) || item);
        setBounded(threads, key, {items: merged, at: Date.now()}, MAX_THREADS);

        return merged;
    } catch (_e) {
        return null;
    }
}

/**
 * Read a cached conversation.
 * @param {string} threadKey Conversation key.
 * @returns {Array<Object>|null} The cached items, or null when absent/stale.
 */
export function getCachedMailThread(threadKey) {
    try {
        const key = str(threadKey);
        if (!key) {
            return null;
        }
        const entry = threads.get(key);
        if (!isFresh(entry)) {
            return null;
        }

        return Array.isArray(entry.items) && entry.items.length ? entry.items : null;
    } catch (_e) {
        return null;
    }
}

/**
 * Fire-and-forget fetch of a single message into the cache. De-dupes concurrent
 * calls for the same id, skips when a full body is already cached, mirrors the
 * result to disk and NEVER rejects.
 * @param {string|{messageId?: string, id?: string}} idOrOptions Message id (or `{messageId}`).
 * @returns {Promise<Object|null>} The fetched message, or null on any failure.
 */
export function prefetchMailMessage(idOrOptions) {
    const id = typeof idOrOptions === 'object' && idOrOptions !== null
        ? str(idOrOptions.messageId || idOrOptions.id)
        : str(idOrOptions);
    if (!id) {
        return Promise.resolve(null);
    }

    const cached = getCachedMailMessage(id);
    if (cached && !cached.__mailStub && bodyScore(cached) > 0) {
        return Promise.resolve(cached);
    }

    const key = `msg:${id}`;
    const existing = inflight.get(key);
    if (existing) {
        return existing;
    }

    const promise = Promise.resolve()
        .then(() => mailApi.getMailMessage(id))
        .then((result) => {
            const message = result?.message || result;
            if (!message || typeof message !== 'object') {
                return null;
            }
            const merged = primeMailMessage({...message, id: idOf(message) || id});
            if (merged) {
                persistMessage(merged);
            }

            return merged;
        })
        .then(
            (value) => {
                inflight.delete(key);

                return value;
            },
            () => {
                inflight.delete(key);

                return null;
            },
        );

    inflight.set(key, promise);

    return promise;
}

/**
 * Fire-and-forget fetch of a whole conversation into the cache. De-dupes,
 * skips when a fresh thread is already cached and NEVER rejects.
 * @param {string|{threadKey?: string}} keyOrOptions Conversation key (or `{threadKey}`).
 * @returns {Promise<Array<Object>|null>} The fetched items, or null on any failure.
 */
export function prefetchMailThread(keyOrOptions) {
    const threadKey = typeof keyOrOptions === 'object' && keyOrOptions !== null
        ? str(keyOrOptions.threadKey)
        : str(keyOrOptions);
    if (!threadKey) {
        return Promise.resolve(null);
    }

    const cached = getCachedMailThread(threadKey);
    if (cached) {
        return Promise.resolve(cached);
    }

    const key = `thr:${threadKey}`;
    const existing = inflight.get(key);
    if (existing) {
        return existing;
    }

    const promise = Promise.resolve()
        .then(() => mailApi.getMailThread(threadKey))
        .then((result) => {
            const items = Array.isArray(result?.items) ? result.items : (Array.isArray(result) ? result : []);
            if (!items.length) {
                return null;
            }

            return cacheMailThread(threadKey, items);
        })
        .then(
            (value) => {
                inflight.delete(key);

                return value;
            },
            () => {
                inflight.delete(key);

                return null;
            },
        );

    inflight.set(key, promise);

    return promise;
}

/**
 * Build + cache a placeholder message from a push payload so the reader can
 * paint sender / subject / preview on its first frame, long before the network
 * answers. The stub is flagged `__mailStub: true` and is always replaced by the
 * real message as soon as it lands.
 *
 * Reads both camelCase and all-lowercase keys — FCM `data` values arrive as
 * strings and different producers spell them differently.
 *
 * @param {Object} data Push `data` payload (FCM or local notification).
 * @returns {Object|null} The cached stub, or null when the payload has no message id.
 */
export function primeMailStubFromPush(data) {
    try {
        if (!data || typeof data !== 'object') {
            return null;
        }
        const id = pick(data, 'mailMessageId', 'mailmessageid', 'messageId', 'messageid');
        if (!id) {
            return null;
        }

        const subject = pick(data, 'subject', 'mailSubject', 'mailsubject', 'title');
        const fromName = pick(data, 'fromName', 'fromname', 'senderName', 'sendername', 'from');
        const fromEmail = pick(data, 'fromEmail', 'fromemail', 'senderEmail', 'senderemail');
        // Always sanitized: a legacy payload may still carry HTML/markdown here.
        const preview = toPlainMailPreview(pick(data, 'preview', 'previewText', 'previewtext', 'snippet', 'body'));
        const receivedAt = pick(data, 'receivedAt', 'receivedat', 'sentAt', 'sentat');
        const threadKey = pick(data, 'threadKey', 'threadkey');
        // `mail.opened` / `mail.send_failed` are about a message the user SENT.
        const direction = pick(data, 'direction') === 'outgoing' ? 'outgoing' : 'incoming';

        const stub = {
            id,
            __mailStub: true,
            direction,
            ...(subject ? {subject} : {}),
            ...(threadKey ? {threadKey} : {}),
            ...(preview ? {previewText: preview} : {}),
            ...(receivedAt ? {receivedAt} : {}),
            ...(fromName || fromEmail
                ? {from: [{...(fromName ? {name: fromName} : {}), ...(fromEmail ? {email: fromEmail} : {})}]}
                : {}),
        };

        const merged = primeMailMessage(stub);
        // Mirror to disk: on Android the background push handler runs in a
        // separate JS runtime, so this is the only way the UI process sees it.
        AsyncStorage.setItem(DISK_STUB_KEY, JSON.stringify({at: Date.now(), message: merged || stub}))
            .catch(() => {});

        return merged;
    } catch (_e) {
        return null;
    }
}

/**
 * Drop everything (memory + disk mirror). Called on logout / account removal so
 * a stale body can never be shown to a different mailbox.
 * @returns {void}
 */
export function clearMailCache() {
    try {
        messages.clear();
        threads.clear();
        inflight.clear();
    } catch (_e) {
        // ignore
    }
    (async () => {
        try {
            const raw = await AsyncStorage.getItem(DISK_INDEX_KEY);
            const ids = raw ? JSON.parse(raw) : [];
            const keys = [DISK_STUB_KEY, DISK_INDEX_KEY].concat(
                Array.isArray(ids) ? ids.map((id) => `${DISK_MESSAGE_PREFIX}${id}`) : [],
            );
            await AsyncStorage.multiRemove(keys);
        } catch (_e) {
            // best effort
        }
    })();
}

// Warm the memory cache from the disk mirror as early as this module is
// imported (App boot), so a cold, push-driven launch finds the stub/body that
// the background process fetched. Fire-and-forget by design: nothing waits.
hydrateMailCacheFromDisk().catch(() => {});

export default {
    toPlainMailPreview,
    primeMailMessage,
    primeMailMessages,
    getCachedMailMessage,
    patchCachedMailMessage,
    dropCachedMailMessage,
    cacheMailThread,
    getCachedMailThread,
    prefetchMailMessage,
    prefetchMailThread,
    primeMailStubFromPush,
    hydrateMailCacheFromDisk,
    clearMailCache,
};
